// Presentation helpers for pathways that opt in with `refinedFlowUi`. No clinical rules live here.

import type { Pathway } from "../types/pathway.types";
import { calculateAgeYearsFromDateOfBirth } from "../utils/ageCalculator";

export function refinedStep(
  pathway: Pathway,
  answeredCount: number,
): { label: string; percent: number } | null {
  const ui = pathway.refinedFlowUi;
  if (!ui) return null;
  const step = ui.stepOffset + answeredCount + 1;
  const total = Math.max(ui.stepTotal, step);
  return { label: `Step ${step} of ${total}`, percent: (step / total) * 100 };
}

export function flowPhaseProgress(
  pathway: Pathway,
  answeredCount: number,
): { phase: NonNullable<Pathway["flowPhase"]>["phase"]; step: number; total: number } | null {
  const fp = pathway.flowPhase;
  if (!fp) return null;
  const ui = pathway.refinedFlowUi;
  const step = (ui?.stepOffset ?? fp.stepOffset ?? 0) + answeredCount + 1;
  return { phase: fp.phase, step, total: Math.max(ui?.stepTotal ?? fp.stepTotal ?? step, step) };
}

/** The read-only age follows the confirmed date of birth, so an amended date of birth never leaves a stale age behind. */
export function withDerivedAge(
  pathway: Pathway,
  confirmed: Record<string, unknown>,
): Record<string, unknown> {
  if (!pathway.refinedFlowUi) return confirmed;
  const derived = pathway.demographicConfiguration.bannerFields.find((f) => f.derivedFromDateOfBirth);
  const age = derived ? calculateAgeYearsFromDateOfBirth(confirmed.dateOfBirth as string) : null;
  return derived && age !== null ? { ...confirmed, [derived.field]: age } : confirmed;
}

function unwrap(value: unknown): unknown {
  return value && typeof value === "object" && "value" in value ? (value as { value: unknown }).value : value;
}

/** Sex as confirmed on the demographics screen when it was carried into this pathway, else the patient's own. */
export function confirmedBiologicalSex(
  patient: unknown,
  context: Record<string, unknown>,
): string | undefined {
  const carried = unwrap((context.demographicConfirmed as Record<string, unknown> | undefined)?.biologicalSex);
  const sex = carried ?? unwrap((patient as { biologicalSex?: unknown } | undefined)?.biologicalSex);
  return typeof sex === "string" ? sex : undefined;
}

/** Ticked flags (and their short names) for the checklist a refined stop screen is built from; undefined when unknown. */
export function alarmFlagContext(
  pathway: Pathway,
  sourceNodeId: string | undefined,
  answers: Map<string, unknown>,
): { tickedFlagIds?: string[]; flagNames?: Record<string, string> } {
  if (!pathway.refinedFlowUi || !sourceNodeId) return {};
  const source = pathway.nodes.find((n) => n.id === sourceNodeId) as
    | { items?: { id: string; text: string }[] }
    | undefined;
  const ticked = answers.get(sourceNodeId);
  if (!source?.items || !Array.isArray(ticked) || ticked.length === 0) return {};
  return {
    tickedFlagIds: ticked as string[],
    flagNames: Object.fromEntries(source.items.map((i) => [i.id, i.text.split(" — ")[0]!.trim()])),
  };
}
